package pl.nickers.match3.ecs

import pl.nickers.match3.SoundEvent
import kotlin.math.abs

internal class InputSystem(
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private sealed class Event {
        data class Click(val row: Int, val col: Int) : Event()
        data class Drag(val fromRow: Int, val fromCol: Int, val toRow: Int, val toCol: Int) : Event()
    }

    private val pendingEvents = mutableListOf<Event>()
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var selectedMapper: ComponentMapper<SelectedComponent>
    private lateinit var swappingMapper: ComponentMapper<SwappingComponent>
    private lateinit var draggableMapper: ComponentMapper<DraggableComponent>
    private lateinit var fusedBombMapper: ComponentMapper<FusedBombComponent>
    private lateinit var activatedLaserMapper: ComponentMapper<ActivatedLaserComponent>
    private lateinit var activatedSwirlMapper: ComponentMapper<ActivatedSwirlComponent>
    private lateinit var pendingSwapNewMapper: ComponentMapper<PendingSwapComponent>

    override fun initialize() {
        posMapper = world.mapper()
        selectedMapper = world.mapper()
        swappingMapper = world.mapper()
        draggableMapper = world.mapper()
        fusedBombMapper = world.mapper()
        activatedLaserMapper = world.mapper()
        activatedSwirlMapper = world.mapper()
        pendingSwapNewMapper = world.mapper()
    }

    private fun inputActive(board: BoardStateComponent): Boolean =
        board.phase == GamePhase.INPUT

    fun enqueueClick(row: Int, col: Int) {
        pendingEvents.add(Event.Click(row, col))
    }

    fun enqueueDragSwap(fromRow: Int, fromCol: Int, toRow: Int, toCol: Int) {
        pendingEvents.add(Event.Drag(fromRow, fromCol, toRow, toCol))
    }

    /** Whether this entity can be activated by a double-click (per its piece definition). */
    private fun isSpecialTile(entityId: Int): Boolean =
        world.hasActivation(entityId, pieceSet, Activation.DOUBLE_CLICK)

    /**
     * Dispatches the on-activate effect of [entityId]'s piece definition.
     * Laser → activate beam; swirl → fuse + arm with most-common colour;
     * any 3x3-blast piece (bomb / cross-bomb) → fuse.
     */
    private fun activateSpecialTile(entityId: Int, board: BoardStateComponent) {
        when (world.onActivateOf(entityId, pieceSet)?.effect) {
            EffectKind.LINE_CLEAR ->
                activateLaser(world, entityId, pieceSet, activatedLaserMapper)
            EffectKind.COLLECT_COLOUR -> {
                fusedBombMapper.set(entityId, FusedBombComponent())
                activatedSwirlMapper.set(
                    entityId,
                    ActivatedSwirlComponent(targetPieceId = world.mostCommonJellyPieceId(pieceSet) ?: PieceIds.EMBER_SEED),
                )
            }
            EffectKind.AREA_BLAST_3X3 ->
                fusedBombMapper.set(entityId, FusedBombComponent())
            else -> { /* unknown / not activatable in place */ }
        }
        selectedMapper.remove(entityId)
        world.enqueueAudioEvent(SoundEvent.SWAP_VALID)
        board.didWork = true
    }

    /**
     * Whether enqueueing a click at this cell would directly activate a special tile
     * (bomb or laser already selected and clicked again). Used to snapshot world state
     * before the player move without duplicating event handling.
     */
    fun wouldDoubleClickActivate(row: Int, col: Int): Boolean {
        val board = world.boardState() ?: return false
        if (!inputActive(board)) return false
        val clickedEntity = world.findEntityAt(row, col) ?: return false
        if (!draggableMapper.has(clickedEntity)) return false
        val selectedEntity = findSelectedEntity() ?: return false
        if (selectedEntity != clickedEntity) return false
        return isSpecialTile(clickedEntity)
    }

    /**
     * Whether enqueueing a click at this cell (with current selection) would start a swap.
     * Used to snapshot world state before the player move without duplicating event handling.
     */
    fun wouldClickStartSwap(row: Int, col: Int): Boolean {
        val board = world.boardState() ?: return false
        if (!inputActive(board)) return false
        val clickedEntity = world.findEntityAt(row, col) ?: return false
        if (!draggableMapper.has(clickedEntity)) return false
        val selectedEntity = findSelectedEntity() ?: return false
        if (selectedEntity == clickedEntity) return false
        if (!isAdjacentEntities(selectedEntity, clickedEntity)) return false
        return draggableMapper.has(clickedEntity)
    }

    /** Whether enqueueing this drag would start a swap. */
    fun wouldDragStartSwap(fromRow: Int, fromCol: Int, toRow: Int, toCol: Int): Boolean {
        val board = world.boardState() ?: return false
        if (!inputActive(board)) return false
        val entityA = world.findEntityAt(fromRow, fromCol) ?: return false
        val entityB = world.findEntityAt(toRow, toCol) ?: return false
        if (!draggableMapper.has(entityA) || !draggableMapper.has(entityB)) return false
        return isAdjacentEntities(entityA, entityB)
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        if (!inputActive(board)) {
            pendingEvents.clear()
            return
        }
        val events = pendingEvents.toList()
        pendingEvents.clear()
        for (event in events) {
            when (event) {
                is Event.Click -> handleCellClick(event.row, event.col, board)
                is Event.Drag -> handleDragSwap(event, board)
            }
        }
    }

    private fun handleCellClick(row: Int, col: Int, board: BoardStateComponent) {
        if (world.isHole(row, col)) {
            findSelectedEntity()?.let { selectedMapper.remove(it) }
            board.emptyCellSelection = row to col
            return
        }
        val clickedEntity = world.findEntityAt(row, col)
        if (clickedEntity == null) {
            findSelectedEntity()?.let { selectedMapper.remove(it) }
            board.emptyCellSelection = row to col
            return
        }
        if (!draggableMapper.has(clickedEntity)) return
        val selectedEntity = findSelectedEntity()
        board.emptyCellSelection = null
        when {
            selectedEntity == null ->
                selectedMapper.set(clickedEntity, SelectedComponent())
            selectedEntity == clickedEntity -> {
                if (isSpecialTile(clickedEntity)) {
                    activateSpecialTile(clickedEntity, board)
                } else {
                    selectedMapper.remove(clickedEntity)
                }
            }
            isAdjacentEntities(selectedEntity, clickedEntity) -> {
                selectedMapper.remove(selectedEntity)
                if (draggableMapper.has(clickedEntity)) {
                    triggerSwap(selectedEntity, clickedEntity, board)
                }
            }
            else -> {
                selectedMapper.remove(selectedEntity)
                selectedMapper.set(clickedEntity, SelectedComponent())
            }
        }
    }

    private fun handleDragSwap(event: Event.Drag, board: BoardStateComponent) {
        if (world.isHole(event.fromRow, event.fromCol) || world.isHole(event.toRow, event.toCol)) return
        val entityA = world.findEntityAt(event.fromRow, event.fromCol) ?: return
        val entityB = world.findEntityAt(event.toRow, event.toCol) ?: return
        if (!draggableMapper.has(entityA) || !draggableMapper.has(entityB)) return
        if (!isAdjacentEntities(entityA, entityB)) return
        findSelectedEntity()?.let { selectedMapper.remove(it) }
        triggerSwap(entityA, entityB, board)
    }

    private fun triggerSwap(entityA: Int, entityB: Int, board: BoardStateComponent) {
        val posA = posMapper[entityA]!!
        val posB = posMapper[entityB]!!
        swappingMapper.set(entityA, SwappingComponent(
            sourceRow = posA.row, sourceCol = posA.col,
            targetRow = posB.row, targetCol = posB.col,
        ))
        swappingMapper.set(entityB, SwappingComponent(
            sourceRow = posB.row, sourceCol = posB.col,
            targetRow = posA.row, targetCol = posA.col,
        ))
        world.enqueueAudioEvent(SoundEvent.SWAP_VALID)
        pendingSwapNewMapper.set(entityA, PendingSwapComponent(
            fromRow = posA.row, fromCol = posA.col,
            toRow = posB.row, toCol = posB.col,
        ))
        pendingSwapNewMapper.set(entityB, PendingSwapComponent(
            fromRow = posB.row, fromCol = posB.col,
            toRow = posA.row, toCol = posA.col,
        ))
        board.didWork = true
    }

    private fun findSelectedEntity(): Int? =
        world.getEntitiesForAspect(Aspect.all(SelectedComponent::class)).firstOrNull()

    private fun isAdjacentEntities(entityA: Int, entityB: Int): Boolean {
        val posA = posMapper[entityA]!!
        val posB = posMapper[entityB]!!
        val dr = abs(posA.row - posB.row)
        val dc = abs(posA.col - posB.col)
        return (dr == 1 && dc == 0) || (dr == 0 && dc == 1)
    }
}
